import React, { useContext, useState } from 'react'
import { UseFieldArrayUpdate, UseFormWatch } from 'react-hook-form'
import { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form/dist/types/form'

import helpers from '../../../commonHelpers/helpers'
import BaseNumberCard from '../../../components/user/views/profile/BaseNumberCard'
import { CONST } from '../../../const/const'
import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'
import useToasterHelper from '../../../helpers/ToasterHelper'
import { HandleModalContext } from '../../../layout/mainlayout/MainLayout'
import { IHorseData } from '../../../models/horse/horse.interface'
import FirebaseStorageService from '../../../services/storageService'

interface HorseNumberCardProps {
  icon?: string
  label?: string
  numberKey: string
  nameKey?: string
  expiresKey: string
  imageKey?: string
  saved: boolean
  register: UseFormRegister<IHorseData>
  control: Control<IHorseData>
  onRemove?: () => void
  horseData: IHorseData | null
  setHorseData: (data: IHorseData) => void
  setValue: UseFormSetValue<IHorseData>
  update?: UseFieldArrayUpdate<IHorseData, 'horsesOther'>
  watch: UseFormWatch<IHorseData>
}

export const HorseNumberCard: React.FC<HorseNumberCardProps> = ({
  icon,
  label,
  numberKey,
  expiresKey,
  imageKey,
  nameKey,
  saved,
  register,
  control,
  onRemove,
  horseData,
  setHorseData,
  setValue,
  update,
  watch,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const toastFunctions = useToasterHelper()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Helper to determine if this is a horsesOther field
  const isHorsesOtherField = (key: string): boolean => {
    return key.startsWith('horsesOther.')
  }

  // Helper to get the index from horsesOther.X format
  const getHorsesOtherIndex = (key: string): number | null => {
    if (!isHorsesOtherField(key)) return null

    const match = key.match(/horsesOther\.(\d+)\.number/)
    if (match) {
      return parseInt(match[1], 10)
    }
    return null
  }

  // Determine the image key based on the number key or use the explicit imageKey if provided
  const getImageKey = (numKey: string) => {
    if (imageKey) return imageKey

    if (isHorsesOtherField(numKey)) {
      const index = getHorsesOtherIndex(numKey)
      if (index !== null) {
        return `horsesOther.${index}.image`
      }
    }

    return numKey.replace('Number', 'Image')
  }

  // Get the current image URL from horse data
  const getImageUrl = (): string => {
    if (!horseData) return ''

    // Handle horsesOther fields
    if (isHorsesOtherField(numberKey)) {
      const index = getHorsesOtherIndex(numberKey)
      if (index !== null && horseData.horsesOther) {
        return horseData.horsesOther[index]?.image || ''
      }
      return ''
    }

    // Handle regular fields
    const imgKey = getImageKey(numberKey)
    return (horseData as any)[imgKey] || ''
  }

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<void> => {
    if (!file) {
      toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
      return
    }

    if (!horseData?.id) {
      toastFunctions.info({ message: 'Horse ID is required' })
      return
    }

    try {
      setIsLoading(true)
      const PATH = CONST.DATA.STORAGE.HORSE.NUMBERS_URL.PREFIX

      const downloadUrl = await FirebaseStorageService.uploadFile(
        file,
        `${PATH}/${horseData.id}/${new Date().getTime()}`,
        (percent: number) => {
          if (0) console.log(percent)
        }
      )

      const currFormValues = watch()

      // Handle horsesOther fields
      if (isHorsesOtherField(numberKey)) {
        const index = getHorsesOtherIndex(numberKey)
        if (index !== null && currFormValues.horsesOther && currFormValues.horsesOther[index]) {
          update &&
            update(index, {
              ...currFormValues.horsesOther[index],
              image: `${downloadUrl || ''}`,
            })

          // Also update the horse data state to ensure UI reflects the change
          if (horseData && horseData.horsesOther) {
            const updatedOtherDocs = [...horseData.horsesOther]
            updatedOtherDocs[index] = {
              ...updatedOtherDocs[index],
              image: `${downloadUrl || ''}`,
            }

            setHorseData({
              ...horseData,
              horsesOther: updatedOtherDocs,
            })
          }
        }
      }
      // Handle regular fields
      else {
        const imgKey = getImageKey(numberKey)

        const newHorseData = {
          ...horseData,
          ...currFormValues,
          [imgKey]: downloadUrl || '',
        }

        setValue(imgKey as any, downloadUrl || '')
        setHorseData(newHorseData)
      }

      toastFunctions.success({ message: MESSAGES_CONST.PIC_UPLOADED })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      helpers.logger({
        isError: true,
        message: error,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Image remove handler
  const handleImageRemove = async (): Promise<void> => {
    if (!horseData) return

    const imgUrl = getImageUrl()
    if (!imgUrl) return

    try {
      setIsLoading(true)
      await FirebaseStorageService.deleteFile(imgUrl)

      const currFormValues = watch()

      // Handle horsesOther fields
      if (isHorsesOtherField(numberKey)) {
        const index = getHorsesOtherIndex(numberKey)
        if (index !== null && currFormValues.horsesOther) {
          update &&
            update(index, {
              ...currFormValues.horsesOther[index],
              image: '',
            })

          // Also update the horse data state to ensure UI reflects the change
          if (horseData.horsesOther) {
            const updatedOtherDocs = [...horseData.horsesOther]
            updatedOtherDocs[index] = {
              ...updatedOtherDocs[index],
              image: '',
            }

            setHorseData({
              ...horseData,
              horsesOther: updatedOtherDocs,
            })
          }
        }
      }
      // Handle regular fields
      else {
        const imgKey = getImageKey(numberKey)
        const newHorseData = {
          ...horseData,
          ...currFormValues,
          [imgKey]: '',
        }

        setValue(imgKey as any, '')
        setHorseData(newHorseData)
      }

      toastFunctions.success({
        message: MESSAGES_CONST.IMAGE_REMOVED,
      })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setIsLoading(false)
    }
  }

  // Confirm image removal with modal
  const confirmRemoveImage = async (): Promise<void> => {
    if (!horseData) return

    const imgUrl = getImageUrl()
    if (!imgUrl) return

    return new Promise<void>((resolve) => {
      handleModalContext?.handleModal(true, MODAL_CONSTS.CONFIRM_REMOVE_IMAGE, {
        confirmDelete: () => {
          handleImageRemove().then(() => resolve())
        },
      })
    })
  }

  // Get the date field path based on the field type
  const getDateFieldPath = (): string => {
    if (isHorsesOtherField(numberKey)) {
      const index = getHorsesOtherIndex(numberKey)
      if (index !== null) {
        return `horsesOther.${index}.date`
      }
    }
    return expiresKey
  }

  return (
    <BaseNumberCard
      icon={icon}
      label={label}
      saved={saved}
      control={control}
      onRemove={onRemove}
      onImageUpload={handleImageUpload}
      onImageRemove={confirmRemoveImage}
      isLoading={isLoading}
      imageUrl={getImageUrl()}
      titleField={nameKey ? register(nameKey as any) : undefined}
      numberField={register(numberKey as any)}
      dateFieldName={getDateFieldPath()}
      titlePlaceholder="Enter name"
      numberPlaceholder="Enter number"
    />
  )
}
